import Products from 'merchandise-lib/constants/Product';
import { capFscQuantity, getFscTierSeatApiNames, resolveFscMaxQuantity } from 'pricing-pages-ui-components/fsc-configure/fscCatalogHelpers';
import { FSC_AGENT_PACK_PARAM, getFscPackSelectionFromUrl, parseFscUrlProduct, serializeFscUrlProduct, setFscAgentPackMarker } from 'pricing-pages-ui-components/fsc-configure/fscCartUrl';
import { readUnhandledEnumCase } from 'rpc-client-utils';
const PRODUCTS_PARAM = 'products';
const SEAT_CATEGORIES = ['GTM_SEATS', 'OPS_SEATS'];
const COMMITTED_CREDITS = Products.HUBSPOT_CREDITS_COMMITTED;
function setProducts(params, values) {
  params.delete(PRODUCTS_PARAM);
  for (const value of values) params.append(PRODUCTS_PARAM, value);
}
function readUrlQuantities(params) {
  const quantities = new Map();
  for (const value of params.getAll(PRODUCTS_PARAM)) {
    const product = parseFscUrlProduct(value);
    if ((product === null || product === void 0 ? void 0 : product.skuQuantity) !== undefined && Number.isInteger(product.skuQuantity) && product.skuQuantity >= 0) {
      quantities.set(product.productApiName, product.skuQuantity);
    }
  }
  return quantities;
}
function capQuantity(catalog, apiName, quantity) {
  var _catalog$products;
  return capFscQuantity(quantity, resolveFscMaxQuantity((_catalog$products = catalog.products) === null || _catalog$products === void 0 ? void 0 : _catalog$products[apiName]));
}
function parseFscSeats(urlQuantities, catalog, tier) {
  var _urlQuantities$get;
  const seatApiNames = getFscTierSeatApiNames(catalog, tier);
  if (!seatApiNames) return [];
  const {
    primaryApiName,
    otherSeatApiNames
  } = seatApiNames;
  const toSeat = (productApiName, quantity) => ({
    productApiName,
    quantity: capQuantity(catalog, productApiName, quantity)
  });
  return [toSeat(primaryApiName, Math.max((_urlQuantities$get = urlQuantities.get(primaryApiName)) !== null && _urlQuantities$get !== void 0 ? _urlQuantities$get : 1, 1)), ...otherSeatApiNames.flatMap(apiName => {
    var _urlQuantities$get2;
    const quantity = (_urlQuantities$get2 = urlQuantities.get(apiName)) !== null && _urlQuantities$get2 !== void 0 ? _urlQuantities$get2 : 0;
    return quantity > 0 ? [toSeat(apiName, quantity)] : [];
  })];
}
export function parseFscUrlCart(search, catalog, tier) {
  var _urlQuantities$get3;
  const params = new URLSearchParams(search);
  const urlQuantities = readUrlQuantities(params);
  return {
    seats: parseFscSeats(urlQuantities, catalog, tier),
    creditPackSelection: getFscPackSelectionFromUrl(capQuantity(catalog, COMMITTED_CREDITS, (_urlQuantities$get3 = urlQuantities.get(COMMITTED_CREDITS)) !== null && _urlQuantities$get3 !== void 0 ? _urlQuantities$get3 : 0), params.get(FSC_AGENT_PACK_PARAM))
  };
}
export function withFscProductQuantity(search, apiName, quantity) {
  const params = new URLSearchParams(search);
  const products = params.getAll(PRODUCTS_PARAM).filter(value => {
    var _parseFscUrlProduct;
    return ((_parseFscUrlProduct = parseFscUrlProduct(value)) === null || _parseFscUrlProduct === void 0 ? void 0 : _parseFscUrlProduct.productApiName) !== apiName;
  });
  const nextValue = quantity > 0 ? serializeFscUrlProduct({
    productApiName: apiName,
    skuQuantity: quantity
  }) : null;
  if (nextValue) products.push(nextValue);
  setProducts(params, products);
  return params.toString();
}
export function withFscCreditPackSelection(search, selection) {
  const params = new URLSearchParams(withFscProductQuantity(search, COMMITTED_CREDITS, selection.kind === 'included' ? 0 : selection.committedSkuQuantity));
  setFscAgentPackMarker(params, selection);
  return params.toString();
}
export function withPrimaryProduct(search, apiName) {
  const params = new URLSearchParams(search);
  params.set('primaryProduct', apiName);
  return params.toString();
}
function findTierSeatApiName(catalog, tier, skuCategoryName) {
  var _catalog$productsByTi;
  return (_catalog$productsByTi = catalog.productsByTier) === null || _catalog$productsByTi === void 0 || (_catalog$productsByTi = _catalog$productsByTi[tier]) === null || _catalog$productsByTi === void 0 || (_catalog$productsByTi = _catalog$productsByTi.tierProducts) === null || _catalog$productsByTi === void 0 ? void 0 : _catalog$productsByTi.find(apiName => {
    var _catalog$products2;
    return ((_catalog$products2 = catalog.products) === null || _catalog$products2 === void 0 || (_catalog$products2 = _catalog$products2[apiName]) === null || _catalog$products2 === void 0 ? void 0 : _catalog$products2.skuCategoryName) === skuCategoryName;
  });
}
export function buildFscTierSwitchSearch(search, catalog, toTier) {
  var _catalog$productsByTi2;
  const toTierOption = (_catalog$productsByTi2 = catalog.productsByTier) === null || _catalog$productsByTi2 === void 0 ? void 0 : _catalog$productsByTi2[toTier];
  if (!toTierOption) return search;
  const params = new URLSearchParams(search);
  const urlQuantities = readUrlQuantities(params);
  const nextProducts = params.getAll(PRODUCTS_PARAM).flatMap(value => {
    var _parseFscUrlProduct$p, _parseFscUrlProduct2, _catalog$products3;
    const apiName = (_parseFscUrlProduct$p = (_parseFscUrlProduct2 = parseFscUrlProduct(value)) === null || _parseFscUrlProduct2 === void 0 ? void 0 : _parseFscUrlProduct2.productApiName) !== null && _parseFscUrlProduct$p !== void 0 ? _parseFscUrlProduct$p : '';
    const skuCategoryName = (_catalog$products3 = catalog.products) === null || _catalog$products3 === void 0 || (_catalog$products3 = _catalog$products3[apiName]) === null || _catalog$products3 === void 0 ? void 0 : _catalog$products3.skuCategoryName;
    if (!SEAT_CATEGORIES.includes(skuCategoryName)) return [value];
    const quantity = urlQuantities.get(apiName);
    const nextApiName = findTierSeatApiName(catalog, toTier, skuCategoryName);
    if (!quantity || !nextApiName) return [];
    const nextValue = serializeFscUrlProduct({
      productApiName: nextApiName,
      skuQuantity: capQuantity(catalog, nextApiName, quantity)
    });
    return nextValue ? [nextValue] : [];
  });
  params.set('primaryProduct', toTierOption.productApiName);
  setProducts(params, nextProducts);
  return params.toString();
}
function findFscTermKey(purchaseOptions, search) {
  const params = new URLSearchParams(search);
  const paymentTerm = params.get('paymentTerm');
  const commitmentTerm = params.get('commitmentTerm');
  if (!purchaseOptions || !paymentTerm) return undefined;
  return Object.keys(purchaseOptions).find(key => {
    const option = purchaseOptions[key];
    return readUnhandledEnumCase(option.paymentTerm) === paymentTerm && (!commitmentTerm || readUnhandledEnumCase(option.commitmentTerm) === commitmentTerm);
  });
}
export function resolveFscTerm(purchaseOptions, search) {
  const urlTermKey = findFscTermKey(purchaseOptions, search);
  if (urlTermKey === 'ANNUAL' || urlTermKey === 'MONTHLY') return urlTermKey;
  if (purchaseOptions !== null && purchaseOptions !== void 0 && purchaseOptions.ANNUAL || !(purchaseOptions !== null && purchaseOptions !== void 0 && purchaseOptions.MONTHLY)) return 'ANNUAL';
  return 'MONTHLY';
}
export function withFscTerm(search, option) {
  const params = new URLSearchParams(search);
  params.set('paymentTerm', readUnhandledEnumCase(option.paymentTerm));
  params.set('commitmentTerm', readUnhandledEnumCase(option.commitmentTerm));
  return params.toString();
}